import React from 'react';
import { Link } from 'react-router-dom';
import { Icon } from '@iconify/react';
import { useShop } from '../context/ShopContext';
import CartDrawer from './CartDrawer';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const { cart, toggleCart } = useShop();

  const cartItemCount = cart.reduce((acc, item) => acc + item.quantity, 0);

  return (
    <div className="min-h-screen flex flex-col font-sans text-mauve-text">
      {/* Navigation */}
      <nav className="sticky top-0 z-40 bg-mauve-base/90 backdrop-blur-md border-b border-mauve-medium/20 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between h-16 items-center">
            {/* Logo */}
            <div className="flex-shrink-0 flex items-center">
              <Link to="/" className="font-serif text-2xl font-bold text-mauve-dark tracking-wide">
                Mauvies
                <span className="text-mauve-medium text-lg ml-1 font-sans font-light">Collections</span>
              </Link>
            </div>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center space-x-8">
              {/* Shop link removed */}
              {/* Admin link hidden from plain sight */}
              <button
                onClick={toggleCart}
                className="relative p-2 text-mauve-dark hover:text-mauve-light transition-colors"
                aria-label="Cart"
              >
                <Icon icon="lucide:shopping-bag" width="24" />
                {cartItemCount > 0 && (
                  <span className="absolute top-0 right-0 inline-flex items-center justify-center px-2 py-1 text-xs font-bold leading-none text-white transform translate-x-1/4 -translate-y-1/4 bg-mauve-dark rounded-full">
                    {cartItemCount}
                  </span>
                )}
              </button>
            </div>

            {/* Mobile Menu Button */}
            <div className="md:hidden flex items-center">
              <button
                onClick={toggleCart}
                className="relative p-2 mr-2 text-mauve-dark"
                aria-label="Cart"
              >
                <Icon icon="lucide:shopping-bag" width="24" />
                {cartItemCount > 0 && (
                  <span className="absolute top-0 right-0 inline-flex items-center justify-center px-1.5 py-0.5 text-xs font-bold leading-none text-white bg-mauve-dark rounded-full">
                    {cartItemCount}
                  </span>
                )}
              </button>
              {/* Mobile Menu only needed if we add more pages later, currently just Home */}
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="flex-grow container mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {children}
      </main>

      {/* Footer */}
      <footer className="bg-mauve-dark text-mauve-base py-8 mt-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-4 md:mb-0">
              <h3 className="font-serif text-xl font-bold">Mauvies Collections</h3>
              <p className="text-sm opacity-80 mt-1">Timeless elegance in every stitch.</p>
            </div>
            <div className="flex space-x-6 text-sm">
              <a href="https://www.instagram.com/mauvies_precision_ventures/" target="_blank" rel="noopener noreferrer" className="hover:text-mauve-light transition-colors">Instagram</a>
              <a href="https://wa.me/c/233241092639" target="_blank" rel="noopener noreferrer" className="hover:text-mauve-light transition-colors">WhatsApp</a>
              <a href="tel:+233542986675" className="hover:text-mauve-light transition-colors">Call (054-298-6675)</a>
            </div>
          </div>
          <div className="mt-8 text-center text-xs opacity-60 border-t border-mauve-base/20 pt-4 flex flex-col items-center gap-2">
            <p>&copy; {new Date().getFullYear()} Mauvies Collections. All rights reserved.</p>


          </div>
        </div>
      </footer>

      {/* Cart Drawer */}
      <CartDrawer />
    </div>
  );
};

export default Layout;